import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { usePageMeta } from "@/hooks/usePageMeta";
import { POSTS } from "@/data/posts";

export default function PostPage() {
  const { slug } = useParams();
  const index = POSTS.findIndex((p) => p.slug === slug);
  const post = index !== -1 ? POSTS[index] : null;
  const next = POSTS[(index + 1) % POSTS.length];

  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const articleSchema = post
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.excerpt,
        image: `${base}${post.img}`,
        datePublished: post.dateIso,
        author: { "@type": "Organization", name: "BrandZenith" },
        publisher: { "@type": "Organization", name: "BrandZenith" },
      }
    : undefined;

  usePageMeta({
    title: post ? `${post.title} | BrandZenith Insights` : "Insights | BrandZenith",
    description: post ? post.excerpt : "Thinking from BrandZenith on AI, branding and growth.",
    path: `/insights/${slug}`,
    image: post ? post.img : undefined,
    schema: articleSchema,
  });

  if (!post) return <Navigate to="/insights" replace />;

  return (
    <>
      <Navbar />
      <main data-testid="post-page">
        <section className="relative pt-44 pb-16 overflow-hidden">
          <div className="absolute inset-0 grid-bg" aria-hidden="true" />
          <div
            aria-hidden="true"
            className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[360px] rounded-full bg-[#6D00FF]/15 blur-[140px]"
          />
          <div className="relative max-w-3xl mx-auto px-6">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6"
            >
              {post.category} · {post.date} · {post.readTime}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]"
            >
              {post.title}
            </motion.h1>
          </div>
        </section>

        <section className="relative pb-24 lg:pb-32">
          <div className="max-w-3xl mx-auto px-6">
            <Reveal>
              <div className="relative aspect-[21/9] rounded-lg overflow-hidden border border-white/[0.08] mb-14">
                <img
                  src={post.img}
                  alt={`${post.title} — artwork`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-gradient-to-t from-[#050507]/60 to-transparent"
                />
              </div>
            </Reveal>

            <article data-testid="post-content">
              {post.blocks.map((b, i) =>
                b.h ? (
                  <Reveal key={i} y={20}>
                    <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight mt-12 mb-4">
                      {b.h}
                    </h2>
                  </Reveal>
                ) : (
                  <Reveal key={i} y={20}>
                    <p className="text-base sm:text-lg text-[#A6A6B0] leading-relaxed mb-6">
                      {b.p}
                    </p>
                  </Reveal>
                )
              )}
            </article>

            <div className="mt-16 flex flex-wrap items-center justify-between gap-6 border-t border-white/[0.06] pt-8">
              <Link
                to="/insights"
                data-testid="post-back-link"
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#A6A6B0] hover:text-white transition-colors duration-300"
              >
                <ArrowLeft className="w-4 h-4" /> All Insights
              </Link>
              {next && (
                <Link
                  to={`/insights/${next.slug}`}
                  data-testid="post-next-link"
                  className="group text-right max-w-xs"
                >
                  <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[#A6A6B0]/60 mb-1">
                    Next Insight
                  </span>
                  <span className="font-display text-base sm:text-lg font-extrabold tracking-tight text-white/80 group-hover:text-gradient-purple transition-colors duration-300 flex items-center gap-3 justify-end">
                    {next.title}
                    <ArrowRight className="w-4 h-4 shrink-0 text-[#8B2CFF] transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              )}
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </>
  );
}
